import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { uniqueId, debounce, cloneDeep } from 'lodash';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import { isProduction, mailTemplatesUrl } from '@dashboard/services/env';
import MailIframePreview from '@dashboard/components/MailIframePreview';
import usePreviewKey from '../usePreviewKey';
import getCheckoutPreviewUrl from '@dashboard/services/getCheckoutPreviewUrl';
import { usePreview } from '@dashboard/context/PreviewContext';
import getCheckoutUrl from '@dashboard/services/getCheckoutUrl';
import Checkout from '@repo/checkout/Checkout';
import { checkoutLayouts, CheckoutStateProvider } from '@repo/checkout/hooks/useCheckoutState';
import { checkoutTypes } from '@repo/checkout/enums/checkout-types';
import CheckoutCSS from '@repo/checkout/components/CheckoutCSS';
import { XMarkIcon } from '@heroicons/react/24/solid';
import PagePreviewNavigation from './PagePreviewNavigation';
import { useOrderBumps } from '../scenes/PageProduct/components/OrderBumps/useOrderBumpMutations';
import useGuestQuestions from '../scenes/PageGroupRegistration/useGuestQuestions';
import getUrlFromCdn, { CDNFitOption } from '@dashboard/services/getUrlFromCdn';
import { stripePublicKey, stripePublicKeyTest } from '@dashboard/services/env';
import { CheckoutRouterProvider } from '@repo/checkout/hooks/useCheckoutRouter';
import { useCountryCode } from '@repo/checkout/queries/country-code';
import { CheckoutLocalizationProvider } from '@repo/checkout/hooks/useCheckoutLocalization';
import TranslationProvider from '@repo/checkout/lib/intl/TranslationProvider';
import getIsTestMode from '@repo/checkout/lib/getIsTestMode';
import '@repo/checkout/lib/intl/i18n';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetcher } from '@/dashboard/services/request';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import { pageKey } from '@dashboard/lib/queryKeys';
import getPageTheme from '@repo/checkout/lib/theming/getPageTheme';
import checkIsVariantActive from '@repo/checkout/lib/checkIsVariantActive';
import PreviewFontLoader from '@dashboard/components/PreviewFontLoader';
import checkIsEventSoldOut from '@dashboard/services/event/checkIsEventSoldOut';
import checkIsTicketGroupOnSale from '@dashboard/services/event/checkIsTicketGroupOnSale';
import checkIsTicketGroupSoldOut from '@repo/checkout/lib/checkIsTicketGroupSoldOut';
import checkIsTicketTypeSoldOut from '@repo/checkout/lib/checkIsTicketTypeSoldOut';
import { scopeCss } from '@repo/checkout/lib/containPreviewStyles';
import checkIsTicketTypeOnSale from '@dashboard/services/event/checkIsTicketTypeOnSale';
import usePreviewClickToSection from '../hooks/usePreviewClickToSection';
import AIPreviewHighlights from './AIPreviewHighlights';
import AIPreviewCapture from './AIPreviewCapture';

export default function Preview({ page, previewKey, setPreviewKey }) {
  const [preview, previewDispatch] = usePreview();
  const { isFullscreen } = preview;

  // Handle keyboard shortcuts for fullscreen preview
  useEffect(() => {
    const handleKeydown = e => {
      if (e.key === 'Escape' && isFullscreen) {
        previewDispatch({ type: 'setFullscreen', value: false });
      }

      const isMac = navigator.platform.startsWith('Mac');
      const isOnlyPKey = e.key === 'p' && !e.shiftKey && !e.altKey;
      const hasCorrectModifier = isMac ? e.metaKey : e.ctrlKey;

      if (isOnlyPKey && hasCorrectModifier) {
        e.preventDefault();
        previewDispatch({ type: 'setFullscreen', value: !isFullscreen });
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isFullscreen, previewDispatch]);

  const previewContent = (
    <>
      {preview.type === 'test-checkout' && (
        <TestPaymentsCheckoutPreview page={page} previewKey={previewKey} />
      )}

      <div
        className={clsx('h-full mx-auto', {
          hidden:
            preview.type !== 'checkout' &&
            preview.type !== 'confirmationCheckoutMessage' &&
            preview.type !== 'upsell',
        })}
      >
        <CheckoutPreview page={page} seller={page.seller} previewType={preview.type} />
      </div>

      {preview.type === 'confirmationEmailMessage' && (
        <PreviewWrapper backgroundColor={page.backgroundColor}>
          <ConfirmationEmailPreview page={page} />
        </PreviewWrapper>
      )}

      {preview.type === 'reminderEmailMessage' && (
        <PreviewWrapper backgroundColor={page.backgroundColor}>
          <ReminderEmailPreview page={page} reminderNumber={preview.reminderNumber} />
        </PreviewWrapper>
      )}
    </>
  );

  return (
    <div
      className={clsx({
        'h-[calc(100vh_-_120px_-_var(--top-banner-h,0px))]': !isFullscreen,
        'flex flex-col h-full': isFullscreen,
      })}
    >
      {isFullscreen && (
        <div className="shrink-0 px-4 pt-2">
          <PagePreviewNavigation setPreviewKey={setPreviewKey} isFullscreen />
        </div>
      )}
      <div className={clsx('h-full overflow-auto', { 'flex-1 min-h-0 px-4': isFullscreen })}>
        {previewContent}
      </div>
    </div>
  );
}

function TestPaymentsCheckoutPreview({ page, previewKey, previewType }) {
  const [{ variant }] = usePreview();

  const previewUrl = getCheckoutPreviewUrl(page, page.seller, {
    isPreview: true,
    isTesting: true,
    isTestingPayments: true,
    previewKey,
    variant: variant,
    isConfirmation: previewType === 'confirmationCheckoutMessage',
    usePageId: true,
  });

  let checkoutUrl = getCheckoutUrl(page, page.seller);

  return (
    <BrowserPreviewWrapper
      previewUrl={checkoutUrl}
      usageType={page.usageType}
      variant={variant}
      isTestingPayments={true}
    >
      <div
        className={clsx('origin-top-left h-full', {
          // Consistent scaling for preview - container queries handle layout
          //'scale-75 w-[133.33%] h-[133.33%]': variant !== 'site_embed' && variant !== 'site_upsell_embed',
          // Embeds use full scale
          //'scale-100 !w-full !h-full': variant === 'site_embed' || variant === 'site_upsell_embed',
        })}
      >
        <iframe src={previewUrl} className={clsx('w-full h-full')} />
      </div>
    </BrowserPreviewWrapper>
  );
}

function useDebouncedPage(page) {
  return useDeferredValue(page);
}

function CheckoutPreview({ page }) {
  const [preview] = usePreview();
  const { token } = useAuth();
  const { getPage } = usePageQueries();
  const previewContainerRef = useRef(null);
  usePreviewClickToSection(previewContainerRef);

  const isConfirmation = preview.type === 'confirmationCheckoutMessage';
  const isUpsell = preview.type === 'upsell';

  // Fetch the selected upsell page if we have an upsell page ID set
  // (This will be set when on the one-click-upsell settings page)
  const { data: upsellPage, isLoading: isLoadingUpsellPage } = useQuery({
    queryKey: pageKey(preview.upsellPageId),
    queryFn: () => getPage(preview.upsellPageId),
    enabled: !!preview.upsellPageId,
  });

  // Use the upsell page for preview if we're on the upsell settings page and have selected a page
  // Otherwise use the current page
  const rawPageToPreview = preview.upsellPageId && upsellPage ? upsellPage : page;
  const pageToPreview = useDebouncedPage(rawPageToPreview);

  const checkoutUrl = useMemo(
    () => getCheckoutUrl(pageToPreview, pageToPreview.seller),
    [pageToPreview, pageToPreview.seller],
  );

  const checkoutType = useMemo(() => {
    if (pageToPreview.usageType === 'hosted') {
      return checkoutTypes.CHECKOUT;
    }

    if (pageToPreview.usageType === 'site') {
      if (pageToPreview.siteLayoutType === 'embed') {
        return checkoutTypes.EMBED;
      }

      if (pageToPreview.siteLayoutType === 'popup') {
        return checkoutTypes.POPUP;
      }
    }

    return checkoutTypes.CHECKOUT;
  }, [pageToPreview.usageType, pageToPreview.siteLayoutType]);

  const pathname = useMemo(() => {
    if (preview.type === 'checkout') {
      return '/checkout';
    }

    return '/checkout/confirmation/:paymentId';
  }, [preview.type]);

  const pageUrlId = pageToPreview.slug || pageToPreview._id;
  const sellerUrlId = pageToPreview.seller.name
    ? pageToPreview.seller.name
    : pageToPreview.seller._id;

  // Remount the checkout preview when the product's price structure changes, so
  // the picker / selected-price state (initialized once at mount) re-resolves to
  // the new prices instead of lingering stale until a full page refresh.
  // Design-only edits keep the same key, so the preview still updates in place.
  // NB: this hook must run before the upsell-loading early return below — placing
  // a hook after a conditional return changes the hook count when the upsell
  // preview toggles `isLoadingUpsellPage`, throwing "Rendered fewer hooks".
  const pricePreviewKey = useMemo(() => {
    const p = pageToPreview.product;
    const prices = (p?.prices || []).map(pr => `${pr._id}:${pr.enabled !== false}`).join('|');
    const picker = p?.pricePicker || {};
    // The picker resolves its active tab / selected price once at mount, so the
    // per-axis default and tab order must remount it (changing them via "Price
    // tab settings" otherwise leaves the preview's selection stale).
    return [
      prices,
      p?.defaultPriceId || '',
      p?.pricePicker?.groupBy || 'none',
      JSON.stringify(picker.priceDisplayDefaults || {}),
      JSON.stringify(picker.priceDisplayTabOrder || {}),
      // Remount when the merchant changes the preview price so useCheckoutState
      // re-initializes its selected price to the chosen one.
      preview.previewPriceId || '',
    ].join('#');
  }, [pageToPreview.product, preview.previewPriceId]);

  // The checkout renderer resolves bump offers from `page.orderBumpProducts`,
  // which only the storefront fetch layers attach — the editor's page object
  // never carries it, so the preview grafts it from the order-bumps envelope.
  // Same query key as the editor list, which every bump mutation replaces, so
  // the preview tracks edits live.
  const { orderBumpProducts } = useOrderBumps(pageToPreview._id, {
    enabled: (pageToPreview.orderBumps?.length ?? 0) > 0,
  });
  // Guest questions live apart from the page too, so the preview reads the editor's own query.
  const { data: guestQuestions } = useGuestQuestions(pageToPreview._id, {
    enabled: Boolean(pageToPreview.eventDetails?.attendeeDetails?.enabled),
  });
  const previewPage = useMemo(() => {
    if (!orderBumpProducts.length && !guestQuestions) return pageToPreview;
    return {
      ...pageToPreview,
      ...(orderBumpProducts.length && { orderBumpProducts }),
      ...(guestQuestions && { guestQuestions }),
    };
  }, [pageToPreview, orderBumpProducts, guestQuestions]);

  // Show loading state while fetching upsell page
  if (preview.upsellPageId && isLoadingUpsellPage) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500 dark:text-muted-foreground">Loading upsell preview...</div>
      </div>
    );
  }

  const checkoutComponent = (
    <div
      ref={previewContainerRef}
      id={`checkout-preview-container-${pageToPreview._id}`}
      data-light-scope
      className={clsx('preview-container origin-top-left w-full relative', {
        'h-full': checkoutType !== checkoutTypes.POPUP,
      })}
    >
      <CheckoutPreviewComponent
        key={pricePreviewKey}
        page={previewPage}
        checkoutType={checkoutType}
        isConfirmation={isConfirmation}
        pathname={pathname}
        isPreview={true}
        isTesting={true}
        isTestingPayments={false}
        isUpsell={isUpsell}
      />
      {/* The AI's changes belong to this page, not to an upsell page picked for the preview. */}
      {pageToPreview._id === page._id && (
        <>
          <AIPreviewHighlights containerRef={previewContainerRef} pageType={page.type} />
          <AIPreviewCapture key={page._id} pageId={page._id} containerRef={previewContainerRef} />
        </>
      )}
    </div>
  );

  return (
    <>
      <BrowserPreviewWrapper
        key={pageToPreview._id}
        previewUrl={checkoutUrl}
        variant={preview.variant}
        usageType={pageToPreview.usageType}
        previewType={preview.type} // checkout
      >
        {checkoutComponent}
      </BrowserPreviewWrapper>

      {pageToPreview.status === 'published' && isProduction && (
        <CheckoutEmbedMeasurer
          page={previewPage}
          checkoutType={checkoutTypes.EMBED}
          isConfirmation={false}
          pathname={`/embed/${sellerUrlId}/${pageUrlId}`}
          token={token}
        />
      )}
    </>
  );
}

const CheckoutPreviewComponent = React.memo(
  function CheckoutPreviewComponent({
    page,
    checkoutType,
    isConfirmation,
    pathname,
    isPreview = true,
    isTesting = true,
    isTestingPayments = false,
    isMeasurer = false,
    isUpsell = false,
  }) {
    const [{ deviceSize, colorMode, previewPriceId }] = usePreview();

    const history = useHistory();
    const countryCode = useCountryCode();

    const localization = useMemo(() => {
      const locale = navigator.language;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const country = countryCode;

      return { locale, timezone, country };
    }, [countryCode]);

    const theme = useMemo(() => {
      const mergedTheme = getPageTheme({
        theme: page.theme,
        themeOverrides: page.themeOverrides,
        seller: page.seller,
      });

      return mergedTheme;
    }, [page.theme, page.themeOverrides, page.seller]);

    const pageClass = useMemo(() => {
      // When color scheme is explicitly 'dark' or 'light', always use that mode.
      // Only respect the preview color mode toggle when color scheme is 'system'.
      // undefined/null means the default ('light') was stripped — treat as light.
      const themeColorScheme = theme?.tokens?.colors;
      const effectiveDark =
        themeColorScheme === 'dark'
          ? true
          : themeColorScheme === 'system'
            ? colorMode === 'dark'
            : false;

      // Treat unset hostedLayoutType as Center (the implicit default) so the
      // page background paints for older pages that never explicitly chose a layout.
      const layoutType = page.hostedLayoutType || checkoutLayouts.CENTER;

      // Popup scrolls in the BrowserPreviewWrapper scrim, like the store overlay layout.
      const classes = clsx('cp_page flex flex-col relative', {
        'h-full overflow-y-auto': checkoutType !== checkoutTypes.POPUP,
        'max-w-screen-xs mx-auto w-full': checkoutType === checkoutTypes.POPUP,
        dark: effectiveDark,
        'bg-[var(--cp_background-color)]':
          checkoutType === checkoutTypes.CHECKOUT && layoutType === checkoutLayouts.CENTER,
      });

      return classes;
    }, [colorMode, checkoutType, theme?.tokens?.colors, deviceSize, page.hostedLayoutType]);

    const defaultWidth = 1920;
    const defaultHeight = 1080;

    const productFiltered = useMemo(() => {
      return {
        ...page.product,
        variants: page?.product?.variants?.filter(checkIsVariantActive).map(variant => ({
          ...variant,
          options: variant.options.filter(option => option.status === 'enabled') ?? [],
        })),
      };
    }, [page?.product]);

    // Memoize ticketGroups so selections are retained when only design properties change
    const ticketGroups = useMemo(() => {
      if (page.type !== 'event') return [];

      const clonedPage = cloneDeep(page);
      clonedPage.eventDetails.isSoldOut = false;

      if (checkIsEventSoldOut(clonedPage)) {
        clonedPage.eventDetails.isSoldOut = true;
      }

      const activePageTicketGroups = clonedPage.ticketGroups.filter(
        ticketGroup => ticketGroup.status !== 'archived',
      );
      const activePageTicketTypes = activePageTicketGroups
        .flatMap(ticketGroup => ticketGroup.ticketTypes)
        .filter(ticketType => ticketType.status !== 'archived');

      return clonedPage.ticketGroups.map(ticketGroup => {
        ticketGroup.isSoldOut = false;

        ticketGroup.isOnSale = checkIsTicketGroupOnSale({
          ticketGroup: ticketGroup,
          allTicketGroups: activePageTicketGroups,
          page: clonedPage,
        });

        if (checkIsTicketGroupSoldOut(ticketGroup)) {
          ticketGroup.isSoldOut = true;
        }

        ticketGroup.ticketTypes = ticketGroup.ticketTypes.filter(ticketType => {
          if (ticketType.status === 'disabled') {
            return false;
          }

          const isSoldOut = checkIsTicketTypeSoldOut(ticketType);

          ticketType.isSoldOut = isSoldOut;
          ticketType.isOnSale = checkIsTicketTypeOnSale({
            ticketType: ticketType,
            allTicketTypes: activePageTicketTypes,
            page: clonedPage,
          });
          const maxQuantity = (() => {
            if (ticketType.capacity !== null) {
              const remainingCapacity = ticketType.capacity - ticketType.quantitySold;

              if (remainingCapacity <= ticketType.maxQuantity) {
                return remainingCapacity;
              } else {
                return ticketType.maxQuantity;
              }
            }

            return ticketType.maxQuantity;
          })();
          ticketType.maxQuantity = maxQuantity;

          const remainingCapacity = (() => {
            if (ticketType.capacity === null) return 0;

            const remainingCapacity = ticketType.capacity - ticketType.quantitySold;

            return remainingCapacity;
          })();
          ticketType.remainingCapacity = remainingCapacity;
          ticketType.hasCapacity = ticketType.capacity != null;

          delete ticketType.capacity;
          delete ticketType.quantitySold;

          return true;
        });

        return ticketGroup;
      });
    }, [page.type, page.ticketGroups, page.eventDetails]);

    // Compute pageForPreview with eventDetails.isSoldOut (without cloneDeep since we no longer mutate ticketGroups on it)
    let pageForPreview = page;
    if (page.type === 'event') {
      pageForPreview = {
        ...page,
        eventDetails: {
          ...page.eventDetails,
          isSoldOut: checkIsEventSoldOut(page),
        },
      };
    }

    return (
      <TranslationProvider>
        <CheckoutStateProvider
          value={{
            checkout: {
              ...pageForPreview,
              ticketGroups,
            },
            product: productFiltered,
            seller: page.seller,
            isUpsell: isUpsell,
            options: {
              type: checkoutType,
            },
            isPreview: isPreview,
            isTesting: isTesting,
            isTestingPayments: isTestingPayments,
            isTestMode: getIsTestMode({ page }),
            isScreenshot: false,
            isMeasurer: isMeasurer,
            query: {
              preview: isPreview,
            },
            countryCode,
            publicKey: stripePublicKey,
            publicKeyTest: stripePublicKeyTest,
            isConfirmation: isConfirmation,
            previewSelectedPriceId: previewPriceId, // Preview-only chosen price (multi-price)
            previewDeviceSize: deviceSize, // Pass device size for layout decisions
            previewColorMode: colorMode, // Pass color mode for preview
          }}
        >
          {/* Use the original CheckoutCSS for variables */}
          <CheckoutCSS />

          {/* Load Google Fonts for preview */}
          <PreviewFontLoader theme={theme} />

          {/* Add scoped CSS for preview container */}
          <ScopedPreviewCSS
            containerId={`checkout-preview-container-${page._id}`}
            cssString={previewCss}
          />

          {/* Add scoped CSS for embed measurer */}
          <ScopedPreviewCSS
            containerId={`checkout-embed-measurer-${page._id}`}
            cssString={previewCss}
          />

          {/* Add scoped CSS for custom CSS */}
          <ScopedCustomCSS
            containerId={`checkout-preview-container-${page._id}`}
            customCss={theme?.customCss}
          />

          {/* Add scoped CSS for embed measurer */}
          <ScopedCustomCSS
            containerId={`checkout-embed-measurer-${page._id}`}
            customCss={theme?.customCss}
          />

          <div
            className={clsx(pageClass, {
              'bg-[var(--cp_form-background-color)]':
                checkoutType === checkoutTypes.CHECKOUT &&
                (page.hostedLayoutType || checkoutLayouts.CENTER) !== checkoutLayouts.CENTER &&
                deviceSize === 'mobile',
            })}
            style={{
              ...(checkoutType === checkoutTypes.CHECKOUT &&
                ((page.hostedLayoutType || checkoutLayouts.CENTER) === checkoutLayouts.CENTER ||
                  (page.hostedLayoutType === checkoutLayouts.SPLIT && deviceSize === 'mobile')) &&
                (() => {
                  const layoutKey =
                    page.hostedLayoutType === checkoutLayouts.SPLIT
                      ? 'splitLayout'
                      : 'centerLayout';
                  const bgImage = theme?.layout?.[layoutKey]?.backgroundImage?.url;
                  return {
                    ...(theme?.layout?.styles?.background && {
                      background: theme.layout.styles.background,
                    }),
                    ...(bgImage
                      ? {
                          backgroundImage: `image-set(url("${getUrlFromCdn(bgImage, {
                            width: defaultWidth,
                            height: defaultHeight,
                            fit: CDNFitOption.cover,
                          })}"), url("${getUrlFromCdn(bgImage, {
                            width: defaultWidth * 2,
                            height: defaultHeight * 2,
                            fit: CDNFitOption.cover,
                          })}") 2x)`,
                          backgroundSize: 'cover',
                          backgroundPosition: `var(--cp_background-position)`,
                        }
                      : {}),
                  };
                })()),
            }}
          >
            <CheckoutRouterProvider
              router={{
                push: history.push,
                replace: history.replace,
                pathname,
                search: `?preview=true`,
              }}
            >
              <CheckoutLocalizationProvider localization={localization}>
                <Checkout />
              </CheckoutLocalizationProvider>
            </CheckoutRouterProvider>
          </div>
        </CheckoutStateProvider>
      </TranslationProvider>
    );
  },
  function areEqual(prevProps, nextProps) {
    if (prevProps.checkoutType !== nextProps.checkoutType) return false;
    if (prevProps.isConfirmation !== nextProps.isConfirmation) return false;
    if (prevProps.pathname !== nextProps.pathname) return false;
    if (prevProps.isPreview !== nextProps.isPreview) return false;
    if (prevProps.isTesting !== nextProps.isTesting) return false;
    if (prevProps.isTestingPayments !== nextProps.isTestingPayments) return false;
    if (prevProps.isMeasurer !== nextProps.isMeasurer) return false;
    if (prevProps.isUpsell !== nextProps.isUpsell) return false;

    const prev = prevProps.page;
    const next = nextProps.page;
    if (prev === next) return true;
    if (!prev || !next) return false;

    const replacer = (key, value) => (key === '_lastUpdateTimestamp' ? undefined : value);
    return JSON.stringify(prev, replacer) === JSON.stringify(next, replacer);
  },
);

// we want to measure the height of the embed on various widths
// we will iterate over an array of widths and measure the height of the embed
// we will then store the height in an array
// we will then return the array of heights
export function CheckoutEmbedMeasurer({
  page,
  checkoutType,
  isConfirmation,
  pathname,
  onMeasurementsDone,
  isAdminMigration = false,
  measurementCoolDown = 6000,
  token,
}) {
  const ref = useRef(null);
  const widths = [360, 480, 600, 768, 1280];
  const [currentWidth, setCurrentWidth] = useState(widths[0]);
  const [measurements, setMeasurements] = useState([]);
  const [measurementState, setMeasurementState] = useState('rendered'); // rendered -> measuring -> saving
  const [currentMeasurementIndex, setCurrentMeasurementIndex] = useState(0);
  const lastMeasuredTime = useRef(0);
  const scheduledMeasurementTimeout = useRef(null);
  const sellerId = page.seller._id ?? page.seller;

  function getTimeUntilNextMeasurement() {
    const now = Date.now();
    const timeSinceLastMeasurement = now - lastMeasuredTime.current;
    return Math.max(0, measurementCoolDown - timeSinceLastMeasurement);
  }

  function startMeasuring() {
    setCurrentWidth(widths[0]);
    setCurrentMeasurementIndex(0);
    setMeasurements([]);
    setMeasurementState('measuring');
  }

  function scheduleMeasurement() {
    // Don't schedule if there's already a measurement scheduled
    if (scheduledMeasurementTimeout.current) {
      return;
    }

    const delay = getTimeUntilNextMeasurement();

    // Add 1 second to ensure component is fully rendered
    scheduledMeasurementTimeout.current = setTimeout(() => {
      startMeasuring();
      scheduledMeasurementTimeout.current = null;
    }, delay + 1000);
  }

  function measureHeight() {
    if (measurementState !== 'measuring') return;

    const height = ref.current.offsetHeight;
    setMeasurements(prev => [...prev, { width: currentWidth, height }]);

    // Move to next width or finish measuring
    if (currentMeasurementIndex === widths.length - 1) {
      setMeasurementState('saving');
    } else {
      setCurrentMeasurementIndex(i => i + 1);
      setCurrentWidth(widths[currentMeasurementIndex + 1]);
    }
  }

  const { data: storedMeasurements } = useQuery({
    queryKey: ['checkoutEmbedMeasurements', page._id],
    queryFn: async () => {
      return fetcher(`/sellers/${sellerId}/pages/${page._id}/metadata`, token, {
        method: 'GET',
      });
    },
    enabled: !isAdminMigration,
  });

  const saveMeasurementsMutation = useMutation({
    mutationFn: async measurements => {
      return fetcher(`/sellers/${sellerId}/pages/${page._id}/metadata`, token, {
        method: 'POST',
        body: JSON.stringify({
          embedMeasurements: measurements,
        }),
      });
    },
    onSuccess: () => {
      lastMeasuredTime.current = Date.now();
      scheduledMeasurementTimeout.current = null;
      setMeasurementState('rendered');
      if (typeof onMeasurementsDone === 'function') {
        onMeasurementsDone(true);
      }
    },
    onError: error => {
      setMeasurementState('rendered');
      scheduledMeasurementTimeout.current = null;
      console.error('Error saving measurements:', error);
      if (typeof onMeasurementsDone === 'function') {
        onMeasurementsDone(false);
      }
    },
  });

  // Handle measurement state transitions
  useEffect(() => {
    if (measurementState === 'measuring') {
      const timeoutId = setTimeout(() => {
        measureHeight();
      }, 1000);
      return () => clearTimeout(timeoutId);
    }

    if (measurementState === 'saving') {
      if (!storedMeasurements?.embedMeasurements) {
        saveMeasurementsMutation.mutateAsync(measurements);
      } else {
        const measurementsMatch = measurements.every(
          (measurement, index) =>
            measurement.height === storedMeasurements?.embedMeasurements?.[index]?.height,
        );

        if (!measurementsMatch || isAdminMigration === true) {
          saveMeasurementsMutation.mutateAsync(measurements);
        } else {
          setMeasurementState('rendered');
          scheduledMeasurementTimeout.current = null;
        }
      }
    }
  }, [measurementState, currentMeasurementIndex]);

  // Schedule new measurement when page changes or window is resized
  useEffect(() => {
    const debouncedHandleResize = debounce(() => {
      if (window.innerWidth >= 1024) {
        scheduleMeasurement();
      } else {
        // Clear any scheduled measurement if width goes below 1024
        if (scheduledMeasurementTimeout.current) {
          clearTimeout(scheduledMeasurementTimeout.current);
          scheduledMeasurementTimeout.current = null;
        }
      }
    }, 300);

    // Initial check (run immediately without debounce)
    if (window.innerWidth >= 1024) {
      scheduleMeasurement();
    } else {
      if (scheduledMeasurementTimeout.current) {
        clearTimeout(scheduledMeasurementTimeout.current);
        scheduledMeasurementTimeout.current = null;
      }
    }

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      debouncedHandleResize.cancel();
      if (scheduledMeasurementTimeout.current) {
        clearTimeout(scheduledMeasurementTimeout.current);
        scheduledMeasurementTimeout.current = null;
      }
    };
  }, [page]);

  return (
    <div
      id={`checkout-embed-measurer-${page._id}`}
      style={{ position: 'absolute', left: -9999, top: -9999, width: currentWidth }}
      ref={ref}
    >
      <CheckoutPreviewComponent
        page={page}
        checkoutType={checkoutType}
        isConfirmation={isConfirmation}
        pathname={pathname}
        isPreview={false}
        isTesting={true}
        isTestingPayments={true}
        isMeasurer={true}
      />
    </div>
  );
}

// Create a component that only scopes the custom CSS
function ScopedCustomCSS({ containerId, customCss }) {
  const scopedCustomCss = useMemo(() => {
    if (!customCss) return '';

    // A stray closing brace would end the @scope block early and let the rest
    // of the CSS reach the dashboard, so unbalanced CSS is not injected at all.
    const openBraces = (customCss.match(/{/g) || []).length;
    const closeBraces = (customCss.match(/}/g) || []).length;

    if (openBraces !== closeBraces) {
      console.warn(
        `[ScopedCustomCSS] Invalid CSS detected: unbalanced braces (${openBraces} opening, ${closeBraces} closing). CSS will not be applied to prevent style leaks.`,
      );
      return '';
    }

    return scopeCss(customCss, `#${containerId}`);
  }, [customCss, containerId]);

  if (!scopedCustomCss) return null;

  return <style dangerouslySetInnerHTML={{ __html: scopedCustomCss }} />;
}

function ScopedPreviewCSS({ containerId, cssString }) {
  const scopedCss = useMemo(() => {
    if (!cssString) return '';

    // Scope CSS variable roots, including ones nested inside @media blocks.
    const cssWithScopedRoot = cssString.replaceAll(':root', `#${containerId}`);

    // Prefix all selectors with the container ID to scope them
    return cssWithScopedRoot.replace(/([^{]+)({[^}]*})/g, (match, selector, rules) => {
      // Skip @media queries and other at-rules
      if (selector.trim().startsWith('@')) return match;

      // Split multiple selectors and prefix each one
      const selectors = selector.split(',');
      const prefixedSelectors = selectors
        .map(s => {
          const trimmed = s.trim();
          if (!trimmed) return trimmed;
          if (trimmed.startsWith(`#${containerId}`)) return trimmed;
          return `#${containerId} ${trimmed}`;
        })
        .join(', ');

      return `${prefixedSelectors} ${rules}`;
    });
  }, [cssString, containerId]);

  return <style dangerouslySetInnerHTML={{ __html: scopedCss }} />;
}

const BrowserPreviewWrapper = ({ children, previewUrl, variant, usageType, isTestingPayments }) => {
  const [{ deviceSize }] = usePreview();

  const deviceWidth = useMemo(() => {
    switch (deviceSize) {
      case 'mobile':
        return '390px';
      case 'tablet':
        return '768px';
      case 'desktop':
      default:
        return '100%';
    }
  }, [deviceSize]);

  const containerClassName = useMemo(
    () =>
      clsx('pb-4 h-full', {
        'mx-auto': variant === 'site_embed' || variant === 'site_upsell_embed',
      }),
    [variant],
  );

  const contentClassName = useMemo(
    () =>
      clsx('flex-1 min-h-0 relative', {
        'bg-neutralgray':
          usageType === 'site' &&
          variant !== 'site_embed' &&
          variant !== 'site_upsell_embed' &&
          variant !== 'site_popup',
        // Same scrim as the store overlay layout (py-4 sm:py-8 px-4), keyed to the
        // preview frame width so the mobile preview gets the mobile padding.
        'bg-black/40 overflow-y-auto px-4 py-4 @min-[640px]:py-8':
          variant === 'site_popup' && !isTestingPayments,
      }),
    [usageType, variant, isTestingPayments],
  );

  const displayUrl = previewUrl ? previewUrl.split('?')[0] : previewUrl;

  return (
    <div className={containerClassName}>
      <div
        className={clsx(
          '@container flex flex-col bg-white dark:bg-background border border-gray-100 dark:border-border overflow-hidden h-full mx-auto w-full rounded-t-lg rounded-b-lg',
        )}
        style={{
          maxWidth: deviceWidth,
        }}
      >
        <div className="shrink-0 relative flex items-center justify-between bg-white dark:bg-background px-2 py-1 border-b border-gray-100 dark:border-border rounded-t-lg">
          <div className="absolute left-2 top-[7px] flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
          </div>

          <p className="text-gray-400 dark:text-muted-foreground text-xs mx-auto w-2/3 truncate text-center">
            {usageType === 'site' && 'https://yourwebsite.com'}
            {usageType === 'hosted' && displayUrl ? displayUrl : '\u00A0'}
          </p>
        </div>

        <div data-light-scope className={contentClassName}>
          {variant === 'site_popup' && !isTestingPayments && (
            <button
              className="cp_pop-up-close ml-auto p-1 top-4 right-4 absolute transition-colors bg-neutralgray hover:bg-gray-100 dark:hover:bg-muted rounded-full z-50"
              onClick={() => null}
              disabled
            >
              <XMarkIcon
                width="20px"
                height="20px"
                className="transition-colors fill-current text-gray-700 dark:text-muted-foreground"
              />
            </button>
          )}

          {children}
        </div>
      </div>
    </div>
  );
};

const PreviewWrapper = ({ backgroundColor, children }) => (
  <div
    className={clsx('cp_preview flex flex-col h-full overflow-y-auto', {
      'bg-neutralgray dark:bg-background': !backgroundColor,
    })}
  >
    {children}
  </div>
);

function getEmailPreviewSrc(page, previewKey, previewPriceId) {
  // Pass the previewed price through so the mail-templates route builds its sample
  // from that price (amount / billing) instead of the legacy product scalars.
  const priceParam = previewPriceId ? `&priceId=${previewPriceId}` : '';

  switch (page.type) {
    case 'form':
      return `${mailTemplatesUrl}/submission-confirmation-email/${page._id}?previewKey=${previewKey}`;

    case 'checkout': {
      // Choose the template by the previewed price's billing type — a mixed page's
      // legacy product.type is a single stale value. Fall back to product.type
      // when no price is selected (single-price pages).
      const selectedPrice = (page.product?.prices ?? []).find(
        price => String(price._id) === String(previewPriceId),
      );
      // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type)
      const isRecurring = selectedPrice?.billingType
        ? selectedPrice.billingType === 'recurring'
        : page.product?.type === 'subscription';
      const route = isRecurring ? 'subscription-confirmation-email' : 'payment-confirmation-email';

      return `${mailTemplatesUrl}/${route}/${page._id}?previewKey=${previewKey}${priceParam}`;
    }
    case 'event':
      return `${mailTemplatesUrl}/event-confirmation-email/${page._id}?previewKey=${previewKey}`;
    default:
      return ``;
  }
}

const ConfirmationEmailPreview = ({ page }) => {
  const [previewKey, setPreviewKey] = usePreviewKey();
  const [{ previewPriceId, deviceSize }] = usePreview();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // add timeout to ensure the page has been updated on the server
    // as page may already be updated optimistically
    const timeoutId = setTimeout(() => {
      setPreviewKey(uniqueId());
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [
    page.confirmationEmailSubject,
    page.confirmationEmailMessage,
    page.customizeEmailConfirmation,
    page.sendEmailConfirmation,
    page.confirmationEmailShowLogo,
    page.confirmationEmailShowStoreName,
  ]);

  // useMemo to ensure the email preview src is not recalculated on every render.
  // previewPriceId is a dep so switching the preview price updates the iframe
  // (new priceId + possibly a different template route) immediately.
  const emailPreviewSrc = useMemo(
    () => getEmailPreviewSrc(page, previewKey, previewPriceId),
    [previewKey, previewPriceId],
  );

  return (
    <MailIframePreview src={emailPreviewSrc} previewKey={previewKey} deviceSize={deviceSize} />
  );
};

const previewCss = `
  
`;

const ReminderEmailPreview = ({ page, reminderNumber = 1 }) => {
  const [previewKey, setPreviewKey] = usePreviewKey();
  const [{ previewPriceId, deviceSize }] = usePreview();
  const deviceWidth = { mobile: 390, tablet: 768 }[deviceSize];
  const lastReminderNumberRef = useRef(reminderNumber);
  const reminderKey = `reminder${reminderNumber}`;
  const reminderData = page?.checkoutAbandonment?.emailReminders?.[reminderKey] || {};
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    /**
     * Reminders have multiple tabs. We track whether this is the first time
     * rendering a different reminder email rather than simply the first as seen in other functions
     * in this file.
     */
    if (lastReminderNumberRef.current !== reminderNumber) {
      lastReminderNumberRef.current = reminderNumber;

      return;
    }

    // add timeout to ensure the page has been updated on the server
    // as page may already be updated optimistically
    const timeoutId = setTimeout(() => {
      setPreviewKey(uniqueId());
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [
    reminderData.subject,
    reminderData.body,
    reminderData.buttonText,
    reminderData.customizeEmail,
    page?.checkoutAbandonment?.showStoreLogo,
    page?.checkoutAbandonment?.showStoreName,
    reminderNumber,
  ]);

  const priceParam = previewPriceId ? `&priceId=${previewPriceId}` : '';
  const emailPreviewSrc = useMemo(
    () =>
      `${mailTemplatesUrl}/checkout-abandonment-reminder-email/${page._id}?previewKey=${previewKey}&reminderNumber=${reminderNumber}${priceParam}`,
    [page._id, previewKey, reminderNumber, priceParam],
  );

  return (
    <div className="h-full pb-4">
      {/* At phone or tablet width the email's own frame narrows, so no empty white sits beside it. */}
      <div
        className="relative mx-auto h-full overflow-hidden rounded-lg border border-gray-100 dark:border-border bg-white dark:bg-background"
        style={deviceWidth ? { maxWidth: deviceWidth } : undefined}
      >
        <div className="h-full">
          <iframe
            key={previewKey}
            src={emailPreviewSrc}
            className={clsx(
              'origin-top-left',
              deviceWidth
                ? 'h-full w-full'
                : [
                    'scale-50 w-[200%] h-[calc(100%/0.5)]',
                    'xl:scale-75 xl:w-[133.33%] xl:h-[calc(100%/0.75)]',
                    '3xl:scale-100 3xl:w-full 3xl:h-full',
                  ],
            )}
          />
        </div>
      </div>
    </div>
  );
};
